import { useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { selectActiveJobs, selectCompletedJobs, removeAsyncJob } from '@app/features/asyncJobs/asyncJobsSlice';
import JobStatusItem from './JobStatusItem';

const COMPLETED_DISMISS_DELAY = 10000;
const FAILED_DISMISS_DELAY = 30000;

const JobStatusPanel = () => {
	const dispatch = useAppDispatch();
	const activeJobs = useAppSelector(selectActiveJobs);
	const completedJobs = useAppSelector(selectCompletedJobs);
	const scheduledJobsRef = useRef<Set<number>>(new Set());

	useEffect(() => {
		const timeouts: Map<number, ReturnType<typeof setTimeout>> = new Map();

		completedJobs.forEach(job => {
			if (scheduledJobsRef.current.has(job.id)) return;

			const delay = job.status === 'completed' ? COMPLETED_DISMISS_DELAY : FAILED_DISMISS_DELAY;
			
			if (job.status === 'completed' || job.status === 'failed') {
				scheduledJobsRef.current.add(job.id);
				const timeout = setTimeout(() => {
					dispatch(removeAsyncJob(job.id));
					scheduledJobsRef.current.delete(job.id);
				}, delay);
				timeouts.set(job.id, timeout);
			}
		});

		return () => {
			timeouts.forEach((timeout) => clearTimeout(timeout));
		};
	}, [completedJobs, dispatch]);

	const allVisibleJobs = [
		...activeJobs, 
		...completedJobs,
	].filter(job => job.kind !== 'report');

	if (allVisibleJobs.length === 0) return null;

	return (
		<div style={styles.panel}>
			{allVisibleJobs.map(job => (
				<JobStatusItem key={job.id} job={job} />
			))}
		</div>
	);
};

const styles: Record<string, React.CSSProperties> = {
	panel: {
		position: 'fixed',
		bottom: '24px',
		right: '24px',
		zIndex: 1000,
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		maxHeight: 'calc(100vh - 48px)',
		width: '320px',
		overflowY: 'auto',
	},
};

export default JobStatusPanel;
